import React, { useState, useEffect, useContext } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import GoogleLoginWrapper from './GoogleLoginWrapper';
import LoaderHelper from '../utility/Loading/LoaderHelper';
import AuthService from '../Api_Module/Api_Services/AuthService';
import { ConvertKitService } from '../services/ConvertKitService';
import { Analytics } from '../services/analytics';
import { alertSuccessMessage, alertWarningMessage } from '../utility/CustomAlertMessage';
import { UserContext } from '../context/UserContext';
import ReactGA from 'react-ga';
ReactGA.initialize(import.meta.env.VITE_GA_TRACKING_ID || '');

interface AuthPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referral?: string | null;
  signUp?: boolean;
}

const AuthPopup: React.FC<AuthPopupProps> = ({ isOpen, onClose, referral, signUp = false }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(signUp);
  const [subscriptionConfig, setSubscriptionConfig] = useState<any>(null);
  const userContext = useContext(UserContext);

  useEffect(() => {
    if (isOpen) {
      setIsSignUp(signUp);
      getSubscriptionConfig();
    }
  }, [isOpen, signUp]);

  const getSubscriptionConfig = async () => {
    try {
      const response = await AuthService.getSubscriptionConfig();
      if (response) {
        setSubscriptionConfig(response);
      }
    } catch (error) {
      console.error('Error fetching subscription config:', error);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse: any) => {
    try {
      // console.log('Google Login Success:', credentialResponse);
      LoaderHelper.loaderStatus(true);
      const result = isSignUp
        ? await AuthService.googleSignup(credentialResponse.credential, credentialResponse.userInfo, referral)
        : await AuthService.googleLogin(credentialResponse.credential, credentialResponse.userInfo, referral);

      if (result?.token) {
        sessionStorage.setItem("STOCK_AUTH_TOKEN", result?.token);
        localStorage.removeItem("referralCode");
        if (result.userProfile) {
          sessionStorage.setItem("userProfile", JSON.stringify(result.userProfile));
        }

        // Trigget ConvertKit Subscription if registering
        if (isSignUp && credentialResponse?.userInfo?.email) {
          ConvertKitService.subscribe(
            credentialResponse.userInfo.email,
            credentialResponse.userInfo.name?.split(' ')[0]
          );

          // Track for GA4 (Import to Google Ads) and Meta
          Analytics.event('sign_up', {
            method: 'Google',
            email: credentialResponse.userInfo.email
          });
        }

        LoaderHelper.loaderStatus(false);
        alertSuccessMessage(isSignUp ? 'Account created successfully!' : 'Login successful!');
        onClose();

        // Refresh user context; keep user on the same page so locked content unlocks in-place
        if (userContext?.refreshUser) {
          await userContext.refreshUser();
        }
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      console.error('Google auth error:', error);
      alertWarningMessage(`Google ${isSignUp ? 'signup' : 'login'} failed. Please try again.`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-theme-secondary rounded-lg p-6 max-w-md w-full mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-theme-muted hover:text-theme-primary"
        >
          <X size={24} />
        </button>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-theme-primary mb-2">
            {isSignUp ? 'Join Stock Fortress' : 'Sign In to Your Account'}
          </h2>

          {/* Trial information */}
          {isSignUp ? (<div className={`p-3 rounded-lg mb-4 ${theme === 'dark' ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-200'} border`}>

            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
              Start Your {subscriptionConfig?.trialPeriodDays || 0}-Day Free Trial Today
            </p>
          </div>) : null}
          {/* <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
              Get instant access to premium features - no credit card required
            </p> */}


          <p className="text-theme-muted mb-6">
            {isSignUp
              ? 'Start your journey with professional stock analysis tools and market insights.'
              : 'Continue where you left off and access your premium analysis tools.'
            }
          </p>

          <GoogleLoginWrapper onSuccess={handleGoogleLoginSuccess} />

          <div className="mt-6 text-center">
            <span className="text-theme-secondary">
              {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            </span>
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPopup;
